import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import {
  Messaging,
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { NotificationsService } from '../notifications/notifications.service';

const FCM_TOKEN_STORAGE_KEY = 'FCM_WEB_TOKEN';
const SW_PATH = '/firebase-messaging-sw.js';

/** True when vapidKey is still a placeholder and cannot be used with PushManager. */
function isUsableVapidKey(key: string | null | undefined): boolean {
  const value = (key || '').trim();
  if (!value || value.length < 20) {
    return false;
  }
  const upper = value.toUpperCase();
  if (
    upper.includes('DUMMY') ||
    upper.includes('REPLACE') ||
    upper.includes('PLACEHOLDER')
  ) {
    return false;
  }
  // Web Push applicationServerKey is URL-safe base64 (typically starts with B…).
  return /^[A-Za-z0-9_-]+$/.test(value);
}

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  private readonly baseUrl = environment.baseUrl;
  private app: FirebaseApp | null = null;
  private messaging: Messaging | null = null;
  private cachedToken: string | null = null;
  private registerInFlight: Promise<string | null> | null = null;
  private foregroundAttached = false;
  private ensureStarted = false;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private injector: Injector,
  ) {
    this.cachedToken = sessionStorage.getItem(FCM_TOKEN_STORAGE_KEY);
  }

  /**
   * Start web push after login / authenticated shell. Idempotent + fail-soft.
   * Prefer calling this right after login so the browser permission prompt appears then.
   */
  ensurePush(): void {
    if (this.ensureStarted) {
      // Still (re)attach page listeners if a prior attempt failed mid-way.
      this.attachServiceWorkerMessageListener();
      return;
    }
    this.ensureStarted = true;
    this.attachServiceWorkerMessageListener();
    // Defer one tick so USER_PORTAL token is readable and navigation can start.
    setTimeout(() => {
      void this.registerForPushNotifications().then((token) => {
        if (!token) {
          // Allow a later ensurePush / login to retry after failure.
          this.ensureStarted = false;
        }
      });
    }, 0);
  }

  /**
   * Force a fresh register attempt (e.g. right after login). Resets the
   * one-shot guard so permission can be requested again after logout/login.
   */
  requestPushAfterLogin(): void {
    this.ensureStarted = false;
    this.ensurePush();
  }

  /** Ask permission, get FCM token, POST to backend. Fail-soft. */
  async registerForPushNotifications(): Promise<string | null> {
    if (this.registerInFlight) {
      return this.registerInFlight;
    }

    this.registerInFlight = (async () => {
      try {
        if (!this.authService.isAuthenticated()) {
          return null;
        }

        if (typeof window === 'undefined' || typeof Notification === 'undefined') {
          console.warn('[push] Notification API not available');
          return null;
        }

        const supported = await isSupported().catch(() => false);
        if (!supported) {
          console.warn('[push] Firebase messaging not supported in this browser');
          return null;
        }

        // Web FCM requires a VAPID key (Web Push certificate) in addition to
        // firebaseConfig. Generate it in the same Firebase project — no extra product.
        const vapidKey = environment.firebase?.vapidKey;
        if (!isUsableVapidKey(vapidKey)) {
          console.warn(
            '[push] Web FCM needs firebase.vapidKey (Web Push certificate). ' +
              'Firebase Console → ryno-8382d → Project settings → Cloud Messaging → ' +
              'Web Push certificates → Generate key pair → paste into environment.firebase.vapidKey. ' +
              'apiKey/appId alone are not enough for browsers.',
          );
          return null;
        }

        const messaging = await this.getMessagingInstance();
        if (!messaging) {
          return null;
        }

        // Attach as early as possible — focused-tab pushes only arrive via onMessage.
        this.attachForegroundListener(messaging);
        this.attachServiceWorkerMessageListener();

        const permission = await this.requestPermission();
        if (permission !== 'granted') {
          console.warn('[push] notification permission not granted');
          return null;
        }

        if (await this.isLikelyBrave()) {
          console.warn(
            '[push] Brave detected. If getToken fails with AbortError / push service error, open ' +
              'brave://settings/privacy and enable "Use Google services for push messaging", then reload.',
          );
        }

        this.logPushDiagnostics();

        let swReg = await this.ensureServiceWorker();
        let token: string | null = null;
        try {
          token = await getToken(messaging, {
            vapidKey: vapidKey!,
            serviceWorkerRegistration: swReg,
          });
        } catch (subscribeError) {
          if (!this.isPushServiceAbort(subscribeError)) {
            throw subscribeError;
          }
          console.warn(
            '[push] push subscribe aborted — full SW reset + retry once',
            subscribeError,
          );
          await this.clearPushSubscription(swReg);
          try {
            await swReg.unregister();
          } catch {
            // ignore
          }
          swReg = await this.ensureServiceWorker();
          token = await getToken(messaging, {
            vapidKey: vapidKey!,
            serviceWorkerRegistration: swReg,
          });
        }

        if (!token) {
          console.warn('[push] no FCM web token returned');
          return null;
        }

        this.cachedToken = token;
        sessionStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);

        const registerPayload = {
          fcm_token: token,
          platform: 'web' as const,
          provider: 'fcm' as const,
        };
        console.log('[push] FCM web token', token);
        try {
          await firstValueFrom(
            this.http.post(
              `${this.baseUrl}/v1/user/fcm/update`,
              registerPayload,
            ),
          );
          console.log('[push] FCM token registered with backend');
        } catch (registerError) {
          console.warn(
            '[push] backend FCM token register failed (token still valid locally)',
            registerError,
          );
        }

        return token;
      } catch (error) {
        if (this.isPushServiceAbort(error)) {
          const brave = await this.isLikelyBrave();
          console.warn(
            '[push] PushManager.subscribe failed after reset.\n' +
              (brave
                ? '→ You appear to be on Brave. This exact AbortError is almost always Brave blocking Google push.\n' +
                  '  Fix: brave://settings/privacy → enable "Use Google services for push messaging" → reload.\n' +
                  '  Or retry in Chrome/Edge to confirm keys are fine.\n'
                : '→ Not Brave (or undetectable). Next checks:\n' +
                  '  1) Retry in a clean Chrome profile / Incognito with permissions allowed\n' +
                  '  2) Confirm vapidKey is the public key from Firebase ryno-8382d → Cloud Messaging → Web Push certificates\n' +
                  '  3) DevTools → Application → Clear site data, unregister all SWs, hard-refresh\n' +
                  '  4) Network must allow fcmregistrations.googleapis.com / fcm.googleapis.com\n'),
            error,
          );
        } else {
          console.warn('[push] registerForPushNotifications failed', error);
        }
        return null;
      } finally {
        this.registerInFlight = null;
      }
    })();

    return this.registerInFlight;
  }

  /**
   * Best-effort DELETE of the registered token. Call before clearing auth.
   */
  async unregisterPushNotifications(): Promise<void> {
    this.ensureStarted = false;
    const token =
      this.cachedToken || sessionStorage.getItem(FCM_TOKEN_STORAGE_KEY);

    this.cachedToken = null;
    sessionStorage.removeItem(FCM_TOKEN_STORAGE_KEY);

    if (!token) {
      return;
    }

    try {
      await firstValueFrom(
        this.http.delete(`${this.baseUrl}/v1/user/fcm/update`, {
          body: { fcm_token: token },
        }),
      );
    } catch {
      // Best-effort on logout / network failure.
    }

    try {
      if (this.messaging) {
        await deleteToken(this.messaging);
      }
    } catch {
      // ignore
    }
  }

  private async requestPermission(): Promise<NotificationPermission> {
    if (Notification.permission === 'granted') {
      return 'granted';
    }
    if (Notification.permission === 'denied') {
      return 'denied';
    }
    return Notification.requestPermission();
  }

  private async getMessagingInstance(): Promise<Messaging | null> {
    if (this.messaging) {
      return this.messaging;
    }

    const cfg = environment.firebase;
    if (!cfg?.apiKey || !cfg?.projectId || !cfg?.appId || !cfg?.messagingSenderId) {
      console.warn('[push] incomplete firebase config in environment');
      return null;
    }

    try {
      this.app = getApps().length ? getApp() : initializeApp(cfg);
      this.messaging = getMessaging(this.app);
      return this.messaging;
    } catch (error) {
      console.warn('[push] failed to initialize Firebase messaging', error);
      return null;
    }
  }

  private isPushServiceAbort(error: unknown): boolean {
    const err = error as { name?: string; message?: string } | null;
    const message = err?.message || '';
    return (
      err?.name === 'AbortError' ||
      /push service error/i.test(message) ||
      /Registration failed/i.test(message)
    );
  }

  private async isLikelyBrave(): Promise<boolean> {
    const nav = navigator as Navigator & {
      brave?: { isBrave?: () => Promise<boolean> };
    };
    if (nav.brave?.isBrave) {
      try {
        return await nav.brave.isBrave();
      } catch {
        // fall through
      }
    }
    return /Brave/i.test(navigator.userAgent);
  }

  private logPushDiagnostics(): void {
    const cfg = environment.firebase;
    console.info('[push] diagnostics', {
      origin: location.origin,
      protocol: location.protocol,
      permission: Notification.permission,
      userAgent: navigator.userAgent,
      projectId: cfg?.projectId,
      messagingSenderId: cfg?.messagingSenderId,
      vapidKeyPrefix: (cfg?.vapidKey || '').slice(0, 12) + '…',
      vapidKeyLength: (cfg?.vapidKey || '').length,
    });
  }

  private async clearPushSubscription(
    registration: ServiceWorkerRegistration,
  ): Promise<void> {
    try {
      const existing = await registration.pushManager.getSubscription();
      if (existing) {
        await existing.unsubscribe();
      }
    } catch {
      // ignore
    }
    try {
      if (this.messaging) {
        await deleteToken(this.messaging);
      }
    } catch {
      // ignore
    }
  }

  /**
   * Register messaging SW and wait until *this* worker is active before
   * PushManager.subscribe (avoids race → AbortError / no active worker).
   */
  private async ensureServiceWorker(): Promise<ServiceWorkerRegistration> {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers not supported');
    }

    // Dedicated FCM scope so we don't fight other page SWs (e.g. PWA).
    const registration = await navigator.serviceWorker.register(SW_PATH, {
      scope: '/firebase-cloud-messaging-push-scope',
    });

    await this.waitForServiceWorkerActive(registration);
    return registration;
  }

  private waitForServiceWorkerActive(
    registration: ServiceWorkerRegistration,
  ): Promise<void> {
    const incoming = registration.installing || registration.waiting;
    if (registration.active && !incoming) {
      return Promise.resolve();
    }

    const worker = incoming || registration.active;
    if (!worker) {
      return Promise.resolve();
    }
    if (worker.state === 'activated') {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const onChange = () => {
        if (worker.state === 'activated') {
          worker.removeEventListener('statechange', onChange);
          resolve();
        }
      };
      worker.addEventListener('statechange', onChange);
    });
  }

  private swMessageListenerAttached = false;

  /**
   * SW posts OSINT_FCM_MESSAGE when a background push is handled so the open
   * tab can toast + refresh the bell even if onMessage did not run.
   */
  private attachServiceWorkerMessageListener(): void {
    if (this.swMessageListenerAttached || typeof navigator === 'undefined') {
      return;
    }
    if (!('serviceWorker' in navigator)) {
      return;
    }
    this.swMessageListenerAttached = true;
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      const data = event?.data;
      if (!data || data.type !== 'OSINT_FCM_MESSAGE') {
        return;
      }
      // Background SW already showed the device notification — only refresh inbox.
      console.log('[push] background push acknowledged by SW', data);
      this.refreshInboxBadge();
    });
  }

  private attachForegroundListener(messaging: Messaging): void {
    if (this.foregroundAttached) {
      return;
    }
    this.foregroundAttached = true;

    // Tab focused: FCM delivers here only — show device/browser notification (no toaster).
    onMessage(messaging, (payload) => {
      console.log('[push] foreground FCM message', payload);
      const title =
        payload?.notification?.title ||
        (payload?.data?.['title'] as string | undefined) ||
        'Notification';
      const body =
        payload?.notification?.body ||
        (payload?.data?.['body'] as string | undefined) ||
        '';
      const link =
        (payload?.data?.['link'] as string | undefined) ||
        (payload?.fcmOptions?.link as string | undefined);

      this.refreshInboxBadge();
      void this.showBrowserNotification(title, body, payload?.data, link);
    });
  }

  private refreshInboxBadge(): void {
    try {
      const notifications = this.injector.get(NotificationsService);
      notifications.refreshUnreadCount();
    } catch {
      // ignore
    }
  }

  /**
   * System/browser notification. Prefer ServiceWorkerRegistration.showNotification
   * — Chrome often suppresses `new Notification()` while the tab is focused.
   */
  private async showBrowserNotification(
    title: string,
    body: string,
    data?: Record<string, string> | undefined,
    link?: string,
  ): Promise<void> {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
      console.warn('[push] browser notification skipped — permission not granted');
      return;
    }

    const options: NotificationOptions = {
      body: body || '',
      icon: '/favicon.png',
      badge: '/favicon.png',
      data: { ...(data || {}), link },
      tag:
        (data?.['notification_id'] as string | undefined) ||
        `osint-fcm-${Date.now()}`,
    };

    try {
      let registration: ServiceWorkerRegistration | undefined;
      if ('serviceWorker' in navigator) {
        registration =
          (await navigator.serviceWorker.getRegistration(
            '/firebase-cloud-messaging-push-scope',
          )) ||
          (await navigator.serviceWorker.getRegistration(SW_PATH)) ||
          (await navigator.serviceWorker.ready.catch(() => undefined));
      }

      if (registration?.showNotification) {
        await registration.showNotification(title || 'Notification', options);
        console.log('[push] browser notification via service worker');
        return;
      }

      // Fallback when no SW registration is available.
      const n = new Notification(title || 'Notification', options);
      n.onclick = () => {
        try {
          window.focus();
          if (link && typeof link === 'string' && link.startsWith('/')) {
            window.location.assign(link);
          }
        } catch {
          // ignore
        }
        n.close();
      };
      console.log('[push] browser notification via Notification()');
    } catch (error) {
      console.warn('[push] browser notification failed', error);
    }
  }
}
